/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import {Injectable} from '@angular/core';
import {Router} from "@angular/router";

import {AuthenticationService} from './authentication.service';
import {BaseService} from "./base-service";

import {PropertyPatch} from "../models/property-patch";
import {TagInstance} from '../models/tag-instance';
import {TagDefinition} from '../models/tag-definition';
import {Property} from '../models/property';
import { AssignedTag } from '../models/assigned-tag';
import { PagedResults } from 'app/models/paged-results';
import { RestService } from './rest-service';
import { PagingControls } from 'app/models/paging-controls';
import { PropertySearchDto } from 'app/models/property-search-dto';
import { Url_Base } from 'app/rest-constants';
import { FilterOption } from '../models/filter-option';

@Injectable()
export class TagService extends BaseService {

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private mRestService: RestService
  ) {
    super(router, authenticationService);
  }

  private getUrl(): string {
    return `${Url_Base}/assets/`;
  }

  getNumberTags(): Promise<Number> {
    const callUrl = this.getUrl() + 'count';
    return this.mRestService.get<Number>(callUrl);
  }

  getNumberTagInstances(): Promise<Number> {
    const callUrl = this.getUrl() + 'tags/count';
    return this.mRestService.get<Number>(callUrl);
  }

  getAllTagsForPaging(start: number, size: number, sortOrder: string, filterString: String): Promise<PagedResults<TagDefinition>> {
    const callUrl = this.getUrl() + 'paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;

    return this.mRestService.get<PagedResults<TagDefinition>>(callUrl);
  }

  getAllTagInstancesForPaging(start: number, size: number, sortOrder: string, filterString: String): Promise<PagedResults<TagInstance>> {
    const callUrl = this.getUrl() + 'tags/paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;
    return this.mRestService.get<PagedResults<TagInstance>>(callUrl);
  }

  getAllTagInstancesForPagingWithFiltering(start: number | null, size: number | null, sortOrder: string | null, filterColumn: string | null, filterString: string | null): Promise<PagedResults<TagInstance>> {
    console.log(`getAllTagInstancesForPagingWithFiltering(start: ${start}, size: ${size}, sortOrder: ${sortOrder}, filterColumn: ${filterColumn}, filterString: ${filterString})`);
    let callUrl: string = `${this.getUrl()}tags/paging`;
    
    if (start == null) {
      start = 0;
    }
    if (size == null) {
      size = 10;
    }
    if (sortOrder == null) {
      sortOrder = 'ASC';
    }
    callUrl = `${callUrl}?start=${start}&size=${size}&order=${sortOrder}`;
    
    if (filterColumn != null && filterString != null && filterColumn.length > 0 && filterString.length > 0) {
      callUrl = `${callUrl}&filterColumn=${filterColumn}&filterString=${filterString}`;
    }

    return this.mRestService.get<PagedResults<TagInstance>>(callUrl);
  }

  /**
   * Pages Tag Instances applying multiple filters at once. Each active filter is
   * sent as a matching (filterColumns, filterStrings, filterTypes) triple, and
   * the backend AND's them together.
   */
  getAllTagInstancesForPagingWithFilters(start: number | null, size: number | null, sortOrder: string | null, filters: FilterOption[]): Promise<PagedResults<TagInstance>> {
    if (start == null) {
      start = 0;
    }
    if (size == null) {
      size = 10;
    }
    if (sortOrder == null) {
      sortOrder = 'ASC';
    }

    let callUrl: string = `${this.getUrl()}tags/paging?start=${start}&size=${size}&order=${sortOrder}`;

    (filters || []).forEach(filter => {
      const value = filter == null ? null : filter.apiValue;
      if (filter != null && filter.column != null && value != null && `${value}`.length > 0) {
        callUrl += `&filterColumns=${encodeURIComponent(filter.column)}`;
        callUrl += `&filterStrings=${encodeURIComponent(value)}`;
        callUrl += `&filterTypes=${encodeURIComponent(filter.type)}`;
      }
    });

    return this.mRestService.get<PagedResults<TagInstance>>(callUrl);
  }

  getAllTagDefinitions(): Promise<TagDefinition[]> {
    const rootUrl = this.getUrl();
    console.log("Fetching all tag definitions from: " + rootUrl);
    return this.mRestService.get<TagDefinition[]>(rootUrl);
  }

getTagDefinitionsPage(start: number, size: number, order: string, filterString: string ):  Promise<{ data: TagDefinition[]; size: number }> {
  const callUrl = this.getUrl() + 'paging?start=' + start + "&size=" + size + "&order=" + order + "&filterColumn=name&filterString=" + filterString;
  console.log("Reached here-" + this.mRestService.get<TagDefinition[]>(callUrl));
  return this.mRestService.get<{ data: TagDefinition[]; size: number }>(callUrl);
}

  getTagDefinitionById(id: number): Promise<TagDefinition> {
    const callUrl = this.getUrl() + `${id}`;
    return this.mRestService.get<TagDefinition>(callUrl);
  }

  getTagDefinitionByUuid(uuid: string): Promise<TagDefinition> {
    const callUrl = this.getUrl() + `uuid/${uuid}`;
    return this.mRestService.get<TagDefinition>(callUrl);
  }

  getAllTagProperties(): Promise<Property[]> {
    const callUrl = this.getUrl() + 'properties';
    return this.mRestService.get<Property[]>(callUrl);
  }

  getAllTagPropertiesForMainDisplay(): Promise<Property[]> {
    const callUrl = this.getUrl() + 'properties/main-display';
    return this.mRestService.get<Property[]>(callUrl);
  }

  createTagDefinition(tagDefinition: TagDefinition): Promise<TagDefinition> {
    const callUrl = this.getUrl();
    return this.mRestService.post<TagDefinition>(callUrl, tagDefinition);
  }

  updateTagDefinition(tagDefinition: TagDefinition): Promise<TagDefinition> {
    const callUrl = this.getUrl();
    return this.mRestService.put<TagDefinition>(callUrl, tagDefinition);
  }

  deleteTagDefinitions(ids: number[]): Promise<any> {
    const callUrl = this.getUrl();
    return this.mRestService.delete(callUrl, ids);
  }

  getAllTagInstances(): Promise<TagInstance[]> {
    const rootUrl = this.getUrl() + 'tags';
    return this.mRestService.get<TagInstance[]>(rootUrl);
  }

  getTagInstancesFiltered(start: number, size: number, order: string, filterString: string): Promise<TagInstance[]> {
    const url = this.getUrl() + `tags/filtered?start=` + start + "&size=" + size + "&order=" + order + "&filterColumn=name&filterString=" + filterString;
    return this.mRestService.get<TagInstance[]>(url);
  } 

  getAllUnassignedTagInstances(): Promise<TagInstance[]> {
    const rootUrl = this.getUrl() + 'tags/unassigned';
    return this.mRestService.get<TagInstance[]>(rootUrl);
  }

  /**
   * Returns the unassigned Tag Instances whose Name matches the given filter,
   * limited to the top {@code size} results (defaults to 100 server-side).
   * Used by the Tag selector on the Assets / People Infrastructure pages so the
   * full set of unassigned Tags is never loaded at once.
   */
  getUnassignedTagInstancesFiltered(filterString: string, size: number = 100): Promise<TagInstance[]> {
    const filter = filterString == null ? '' : filterString;
    const callUrl = `${this.getUrl()}tags/unassigned/filtered?filterString=${encodeURIComponent(filter)}&size=${size}`;
    return this.mRestService.get<TagInstance[]>(callUrl);
  }

  getTagInstanceById(id: number | string): Promise<TagInstance> {
    const callUrl = this.getUrl() + `tags/${id}`;
    return this.mRestService.get<TagInstance>(callUrl);
  }

  createTagInstance(tagInstance: TagInstance): Promise<TagInstance> {
    const callUrl = this.getUrl() + 'tags';
    return this.mRestService.post<TagInstance>(callUrl, tagInstance);
  }

  updateTagInstance(tagInstance: TagInstance): Promise<TagInstance> {
    const callUrl = this.getUrl() + 'tags';
    return this.mRestService.put<TagInstance>(callUrl, tagInstance);
  }

  deleteTagInstances(ids: number[]): Promise<any> {
    const callUrl = this.getUrl() + `tags`;
    return this.mRestService.delete(callUrl, ids);
  }

  getAssignedInfoForTag(tagId: number): Promise<AssignedTag> {
    const callUrl = this.getUrl() + `tags/${tagId}/assigned`;
    return this.mRestService.get<AssignedTag>(callUrl);
  }

  unassignTag(tagId: number): Promise<any> {
    const callUrl = this.getUrl() + `tags/${tagId}/assigned`;
    return this.mRestService.delete(callUrl);
  }

  executeFunction(tagInstanceId: number, functionId: number, propertyPatches: PropertyPatch[], responseType?: string): Promise<any> {
    const callUrl = this.getUrl() + `tags/${tagInstanceId}/execute-function?function_id=${functionId}`;
    return this.mRestService.post(callUrl, propertyPatches, responseType);
  }

  uploadTypes(formData: FormData): Promise<void> {
    const callUrl = this.getUrl() + 'types/upload';
    return this.mRestService.upload(callUrl, formData);
  }

  downloadTypes(): Promise<Blob> {
    const callUrl = this.getUrl() + 'types/download';

    return this.mRestService.get(callUrl)
      .then(response => response['_body'] as Blob);
  }

  uploadTags(formData: FormData): Promise<void> {
    const callUrl = this.getUrl() + 'tags/upload';
    return this.mRestService.upload(callUrl, formData);
  }

  downloadTags(tagId: number): Promise<Blob> {
    const callUrl = this.getUrl() + `tags/download/${tagId}`;

    return this.mRestService.get(callUrl)
      .then(response => response['_body'] as Blob);
  }

  /**
   * Calls the Property Search Paged REST API. 
   * Searches for Object Instances with specified properties with a value like a given value. 
   */
  propertySearchTags(propertyUuid: string, dataValue: string, pagingControls?: PagingControls): Promise<PagedResults<TagInstance>> {
    const callUrl = `${this.getUrl()}tags/property-search` + (pagingControls != null? `?${pagingControls.toUrlParamString()}` : "");
    const searchDto= { property_uuid: propertyUuid, data_value: dataValue} as PropertySearchDto;
    console.log("Calling Property Search for Tag Instances with URL: " + callUrl);
    return this.mRestService.post<PagedResults<TagInstance>>(callUrl, searchDto);
  }

}
